import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  UserIcon,
  CalendarIcon,
  MapPinIcon,
  PhoneIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { opdAdmissionAPI, patientAPI } from '../../api';

const OPDAdmissions = () => {
  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [showAdmitModal, setShowAdmitModal] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    date: 'today'
  });

  useEffect(() => {
    loadAdmissions();
  }, [filters]);

  const loadAdmissions = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchQuery,
        status: filters.status !== 'all' ? filters.status : undefined,
        date: filters.date
      };
      const response = await opdAdmissionAPI.getAdmissions(params);
      setAdmissions(response.data?.admissions || []);
    } catch (error) {
      console.error('Error loading admissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdmitPatient = async (patientData) => {
    try {
      const admissionData = {
        patientId: patientData.patientId,
        admissionDate: new Date().toISOString(),
        department: 'OPD',
        chiefComplaint: patientData.complaint,
        vitals: patientData.vitals,
        notes: patientData.notes
      };
      
      await opdAdmissionAPI.admitPatient(admissionData);
      setShowAdmitModal(false);
      loadAdmissions();
    } catch (error) {
      console.error('Error admitting patient:', error);
    }
  };

  const handleDischarge = async (admissionId, dischargeData) => {
    try {
      await opdAdmissionAPI.dischargePatient(admissionId, dischargeData);
      loadAdmissions();
    } catch (error) {
      console.error('Error discharging patient:', error);
    }
  };

  const StatusBadge = ({ status }) => {
    const colors = {
      'admitted': 'bg-blue-100 text-blue-800',
      'in-treatment': 'bg-yellow-100 text-yellow-800',
      'ready-for-discharge': 'bg-green-100 text-green-800',
      'discharged': 'bg-gray-100 text-gray-800',
      'transferred': 'bg-purple-100 text-purple-800'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace('-', ' ').toUpperCase()}
      </span>
    );
  };

  const AdmissionCard = ({ admission }) => (
    <div 
      className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow duration-200 cursor-pointer"
      onClick={() => setSelectedAdmission(admission)}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <UserIcon className="w-6 h-6 text-blue-600" />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {admission.patient?.firstName} {admission.patient?.lastName}
            </h3>
            <p className="text-sm text-gray-500">ID: {admission.patient?.externalId}</p>
          </div>
        </div>
        <StatusBadge status={admission.status} />
      </div>
      
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="flex items-center text-gray-600">
          <CalendarIcon className="w-4 h-4 mr-2" />
          Admitted: {new Date(admission.admissionDate).toLocaleDateString()}
        </div>
        <div className="flex items-center text-gray-600">
          <MapPinIcon className="w-4 h-4 mr-2" />
          Bed: {admission.bedNumber || 'Not assigned'}
        </div>
        <div className="flex items-center text-gray-600">
          <PhoneIcon className="w-4 h-4 mr-2" />
          {admission.patient?.mobile}
        </div>
        <div className="flex items-center text-gray-600">
          <ExclamationTriangleIcon className="w-4 h-4 mr-2" />
          {admission.priority || 'Normal'}
        </div>
      </div>
      
      {admission.chiefComplaint && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>Chief Complaint:</strong> {admission.chiefComplaint}
          </p>
        </div>
      )}
    </div>
  );

  const AdmitPatientModal = ({ isOpen, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
      patientId: '',
      complaint: '',
      notes: '',
      priority: 'normal',
      vitals: {
        temperature: '',
        bloodPressure: '',
        pulse: '',
        respiration: ''
      }
    });
    
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Admit New Patient</h2>
          
          <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Patient ID
                </label>
                <input
                  type="text"
                  value={formData.patientId}
                  onChange={(e) => setFormData({...formData, patientId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="normal">Normal</option>
                  <option value="urgent">Urgent</option>
                  <option value="emergency">Emergency</option>
                </select>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chief Complaint
              </label>
              <textarea
                value={formData.complaint}
                onChange={(e) => setFormData({...formData, complaint: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Temperature (°F)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.vitals.temperature}
                  onChange={(e) => setFormData({
                    ...formData, 
                    vitals: {...formData.vitals, temperature: e.target.value}
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Blood Pressure
                </label>
                <input
                  type="text"
                  placeholder="120/80"
                  value={formData.vitals.bloodPressure}
                  onChange={(e) => setFormData({
                    ...formData, 
                    vitals: {...formData.vitals, bloodPressure: e.target.value}
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pulse (bpm)
                </label>
                <input
                  type="number"
                  value={formData.vitals.pulse}
                  onChange={(e) => setFormData({
                    ...formData, 
                    vitals: {...formData.vitals, pulse: e.target.value}
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Respiration
                </label>
                <input
                  type="number"
                  value={formData.vitals.respiration}
                  onChange={(e) => setFormData({
                    ...formData, 
                    vitals: {...formData.vitals, respiration: e.target.value}
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Admit Patient
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">OPD Admissions</h1>
          <p className="text-gray-600">Manage patient admissions and discharges</p>
        </div>
        <button
          onClick={() => setShowAdmitModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg flex items-center"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Admit Patient
        </button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search patients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div className="flex gap-4">
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="admitted">Admitted</option>
              <option value="in-treatment">In Treatment</option>
              <option value="ready-for-discharge">Ready for Discharge</option>
            </select>
            
            <select
              value={filters.date}
              onChange={(e) => setFilters({...filters, date: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
        </div>
      </div>

      {/* Admissions List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {admissions.map((admission) => (
            <AdmissionCard key={admission._id} admission={admission} />
          ))}
          
          {admissions.length === 0 && (
            <div className="col-span-full text-center py-12">
              <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No admissions found</p>
            </div>
          )}
        </div>
      )}

      {/* Admit Patient Modal */}
      <AdmitPatientModal
        isOpen={showAdmitModal}
        onClose={() => setShowAdmitModal(false)}
        onSubmit={handleAdmitPatient}
      />
    </div>
  );
};

export default OPDAdmissions;